import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProjectEntity } from '@api/entities';
import { DataById } from '../map/types';
import { PresetEntityName, Run } from '@api/presetsApi/types';

export type ProjectPreset = {
    id: string;
    title: string;
    defaultPreset?: boolean;
};

export type AllowedProjectModes = {
    projectPage: boolean;
    gisLayers: boolean;
    gisV2Layers: boolean;
    // runOptimization: boolean;
    biDashboard: boolean;
    sharedMobilityEditor: boolean;
    networkEditor: boolean;
    transitScheduleEditor: boolean;
    advancedConfig: boolean;
    runs: boolean;
    evInfrastructureEditor: boolean;
    scenarioPresetEditor: boolean;
    dvInfrastructureEditor: boolean;
};

export interface IInitialState {
    currentProjectId: string;
    allowedModes: AllowedProjectModes;
    defaultMode: keyof AllowedProjectModes;
    presets: {
        byId: DataById<ProjectPreset>;
        ids: string[];
        selectedPresetId: string;
        presetType: PresetEntityName;
    };
    runs: {
        byId: DataById<Run>;
        ids: string[];
        selectedRunId: string;
        runIdToCompareWith: string;
    };
    projectsById: DataById<ProjectEntity>;
}

export const DEFAULT_ALLOWED_MODES = {
    projectPage: true,
    gisLayers: true,
    gisV2Layers: false,
    runOptimization: false,
    report: false,
    biDashboard: false,
    sharedMobilityEditor: false,
    networkEditor: false,
    transitScheduleEditor: false,
    advancedConfig: false,
    runs: true,
    evInfrastructureEditor: false,
    scenarioPresetEditor: false,
    dvInfrastructureEditor: false,
};

const initialState: IInitialState = {
    currentProjectId: '',
    allowedModes: DEFAULT_ALLOWED_MODES,
    defaultMode: 'projectPage',
    presets: {
        byId: {},
        ids: [],
        selectedPresetId: '',
        presetType: '' as PresetEntityName,
    },
    runs: {
        byId: {},
        ids: [],
        selectedRunId: '',
        runIdToCompareWith: '',
    },
    projectsById: {},
};

const getProjectsById = (projects: ProjectEntity[]): DataById<ProjectEntity> => {
    return projects.reduce((acc, cur) => {
        acc[cur.id!] = cur;
        return acc;
    }, {});
};

const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        addedProjectId(state, action: PayloadAction<string | null | undefined>) {
            state.currentProjectId = action.payload ?? '';
        },
        addedProjectModes(
            state,
            action: PayloadAction<
                | {
                      modes: AllowedProjectModes;
                      defaultMode?: keyof AllowedProjectModes;
                  }
                | undefined
            >
        ) {
            return {
                ...state,
                allowedModes: {
                    ...state.allowedModes,
                    ...(action.payload ? action.payload.modes : DEFAULT_ALLOWED_MODES),
                },
                defaultMode: action?.payload?.defaultMode ?? initialState.defaultMode,
            };
        },
        addedPresets(
            state,
            action: PayloadAction<
                | {
                      byId: DataById<ProjectPreset>;
                      ids: string[];
                      presetType: PresetEntityName;
                  }
                | undefined
            >
        ) {
            const { byId, ids, presetType } = action.payload ?? {};
            state.presets.byId = byId ?? {};
            state.presets.ids = ids ?? [];
            state.presets.presetType = presetType ?? ('' as PresetEntityName);
        },
        addedProjects(state, action: PayloadAction<DataById<ProjectEntity>>) {
            state.projectsById = action.payload;
        },
        setSelectedPreset(state, action: PayloadAction<Partial<{ presetId: string }> | undefined>) {
            const { presetId } = action.payload ?? {};
            return {
                ...state,
                presets: {
                    ...state.presets,
                    selectedPresetId: presetId ?? '',
                },
            };
        },
        addedRuns(
            state,
            action: PayloadAction<{ byId: DataById<Run>; ids: string[] } | undefined>
        ) {
            const { byId, ids } = action.payload ?? {};
            state.runs.byId = byId ?? {};
            state.runs.ids = ids ?? [];
        },
        setSelectedRunId(state, action: PayloadAction<string | undefined>) {
            return {
                ...state,
                runs: {
                    ...state.runs,
                    selectedRunId: action.payload ?? '',
                },
            };
        },
        setRunIdToCompareWith(state, action: PayloadAction<string | undefined>) {
            return {
                ...state,
                runs: {
                    ...state.runs,
                    runIdToCompareWith: action.payload ?? '',
                },
            };
        },
    },
});

export const {
    addedProjectId,
    addedProjectModes,
    addedPresets,
    addedProjects,
    setSelectedPreset,
    addedRuns,
    setSelectedRunId,
    setRunIdToCompareWith,
} = projectsSlice.actions;

export default projectsSlice.reducer;
